import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { validationEmail, validateExchangeDesiredDate } from 'core/validators';
import * as api from 'core/api';
import { G2S } from 'core/g2s';
import * as auth from 'core/auth';
import * as utils from 'core/utils';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

type SupportNewComponentState = 'form' | 'success';

export interface SupportNewComponentModel {
    state: SupportNewComponentState;
    submitted: boolean;
    error: string;
    category: string | null;
    subCategory: string | null;
    description: string | null;
    orderNumber: string;
    exchangeDesiredDate: string | null;
    email: string;
}
function validateSubCategory(value, parentVm): boolean {
    return value !== '' || parentVm.category !== 'baggage' && value == '';
}
@WithRender
@Component({
    validations: {
        model: {
            category: { required },
            subCategory: { validateSubCategory },
            description: { required },
            orderNumber: { minLength: minLength(7), maxLength: maxLength(7) },
            email: { required, minLength: minLength(5), validationEmail },
            exchangeDesiredDate: { validateExchangeDesiredDate }
        }
    }
})
export default class SupportNewComponent extends Vue {
    $auth: auth.AuthService; // Injected in G2S.init
    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    @Prop()
    orderNumber: string;

    model: SupportNewComponentModel = {
        state: 'form',
        submitted: false,
        error: '',
        category: '',
        subCategory: '',
        description: '',
        orderNumber: '',
        email: '',
        exchangeDesiredDate: ''
    }

    refundInfo: api.RefundInfo = {
        clientText: '',
        headerText: '',
        youPaidText: '',
        weWillRefundText: '',
        termText: '',
        buttonAcceptText: '',
        buttonRejectText: '',
        buttonWriteManuallyText: '',
        refundSum: 0,
        paidSum: 0,
        refundSumCurrency: ''
    };

    categoryOptions: utils.SupportCategoryOption[] = [];
    baggageAdditionalOptions: utils.SupportCategoryOption[] = [];

    isVisibleAutovoid = false;
    autovoidTimeout: number | null = null;
    autovoidLabel = {
        adviseText: '',
        accountText: ''
    };

    acceptedFileTypes = [
        'text/plain',
        'image/jpeg',
        'image/pjpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf'
    ];

    selectedFiles = [];

    mounted() {
        let url = G2S.config.desktopUrl + '/support/new/';
        window.location.href = url;
        return;
        this.loadModel();
    }

    loadModel() {
        this.model.state = 'form';
        this.model.submitted = false;
        
        this.categoryOptions = utils.Support.categoryOptions();
        this.baggageAdditionalOptions = utils.Support.baggageOptions();

        if ((<any>this).$route.query.t) {
            this.model.category = (<any>this).$route.query.t;
        }

        if (this.orderNumber) {
            this.model.orderNumber = this.orderNumber;
            (<any>this.$refs).orderNumber.blur();
        } else if ((<any>this).$route.query.g) {
            new api.ReservationService().getReservationStatus((<any>this).$route.query.g, false, false).then(
                (result: api.GetReservationStatusResult) => {
                    this.model.orderNumber = result.reservationInfo.reservationNumber;
                    (<any>this.$refs).orderNumber.blur();
                }
            );
        }

        this.$auth.getSupportText(G2S.config.locale).then(
            (result) => {
                this.autovoidLabel = {
                    adviseText: result.adviseText,
                    accountText: result.accountText
                };
            },
            (error)=>{
                console.log(error);
            }
        );

        if ((<any>this).$route.query.email) {
            this.model.email = (<any>this).$route.query.email;
        } else if (this.$auth.authenticated) {
            this.$auth.getCurrentUser().then(
                (result: api.User) => {
                    this.model.email = result.email || '';
                }
            );
        }
    }

    getAcceptedFileTypes(): string {
        return this.acceptedFileTypes.join(',');
    }

    getDescriptionPlaceholder(): string | null {
        switch (this.model.category) {
            case 'flight_change':
                return i18next.t('label_support_request_description_flight_change');
            case 'flight_refund':
                return i18next.t('label_support_request_description_flight_refund');
            case 'cancel_same_day':
                return i18next.t('label_support_request_description_cancel_same_day');
            case 'baggage':
                return i18next.t('label_support_request_description_baggage');
            case 'changes':
                return i18next.t('label_support_request_description_changes');
            case 'add_services':
                return i18next.t('label_support_request_description_add_services');
            case 'error':
                return i18next.t('label_support_request_description_error');
            default:
                return null;
        }
    }

    formatFileSize(number: number) {
        if (number < 1024) {
            return number + ' b';
        } else if (number > 1024 && number < 1048576) {
            return (number / 1024).toFixed(1) + ' Kb';
        } else if (number > 1048576) {
            return (number / 1048576).toFixed(1) + ' Mb';
        }
    }

    checkErrors() {
        this.model.error = '';
        this.$v.model!.$touch();
        
        if (this.$v.model!.$error) {
            this.model.error = i18next.t('error_fields');
        }

        if ((<any>this).$v.model.orderNumber.$error) {
            this.model.error = i18next.t('error_order_notvalid');
        } else if (!(<any>this).$v.model.email.validationEmail) {
            this.model.error = i18next.t('error_email_wrong');
        } else if ((<any>this).$v.model.exchangeDesiredDate.$error && this.model.category == 'flight_change') {
            this.model.error = 'Дата указана неверно. Проверьте корректность введенных данных и обратите внимание, что для обмена доступны даты, начиная с завтрашнего дня';
        } else if ((this.model.category === 'flight_change'
            || this.model.category === 'flight_refund')
            && !this.model.orderNumber) {
            this.model.error = i18next.t('error_support_request_order_required');
        } else if ((<any>this).$v.model.description.$error) {
            this.model.error = this.model.description
                ? i18next.t('label_support_request_desc_wrong')
                : i18next.t('label_support_request_desc_empty');
        } else if((<any>this).$v.model.subCategory.$error) {
            this.model.error = i18next.t('error_support_sub_category_required');
        }
    }

    submitTicket() {
        // Use form data for file upload
        const request = new FormData();
        request.append('category', <string>this.model.category);
        request.append('description', <string>this.model.description);
        request.append('email', <string>this.model.email);
        request.append('orderNumber', this.model.orderNumber);
        request.append('clientDevice', JSON.stringify(G2S.config.clientDevice));

        if(this.model.subCategory){
            request.append('subCategory', <string>this.model.subCategory);
        }

        if (this.model.category == 'flight_change' && G2S.config.marketCode == 'ru') {
            request.append('exchangeDesiredDate', <string>this.model.exchangeDesiredDate);
        }

        for (let i = 0; i < this.selectedFiles.length; i++) {
            request.append('files[' + i + ']', this.selectedFiles[i]);
        }

        this.model.submitted = true;
        new api.SupportService().create(request).then(
            (result: api.SupportCreateResult) => {
                this.model.submitted = false;
                if (result.success) {
                    (<any>this.$root).$refs.messageDialog.show({
                        persistent: true,
                        title: i18next.t('title_thank_you'),
                        message: i18next.t('label_support_request_success'),
                        onOkCallback: () => {
                            window.location.href = G2S.config.theme !== 'aviasales' ? '/' : 'https://aviasales.ru';
                        }
                    });
                    (<any>this).$ga.sendEvent('support.new', 'submit.success', 'support.new.submit.success', this.gaParams);
                    (<any>this).$ga.reachGoal('support.new.success');
                }
                else {
                    (<any>this).$ga.sendEvent('support.new', 'error', 'support.new.error', this.gaParams);
                    (<any>this.$root).$refs.messageDialog.show({
                        title: i18next.t('error'),
                        message: result.message
                    });
                }
            },
            (error) => {
                this.model.submitted = false;
                (<any>this).$ga.sendEvent('support.new', 'error', 'support.new.server.error', this.gaParams);

                if (error.response) {
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                let errorMessage = i18next.t('error_server_tryagain');

                if (error?.response?.data?.localizedMessage) {
                    errorMessage = error.response.data.localizedMessage;
                }

                (<any>this.$root).$refs.messageDialog.show({
                    title: i18next.t('error'),
                    message: errorMessage
                });
            }
        );
    }

    checkVisibleAutovoid() {
        if (this.model.category === 'cancel_same_day' && String(this.model.orderNumber).length >= 7) {
            this.autovoidTimeout = window.setTimeout(() => {
                this.$auth.checkVoidAvailability(this.model.orderNumber).then(({isAutoVoidAvailable}) => {
                    this.isVisibleAutovoid = isAutoVoidAvailable;
                });
            }, 500);
        } else {
            this.isVisibleAutovoid = false;
        }
    }

    onChangeOrderNumber() {
        if (this.autovoidTimeout) {
            clearTimeout(this.autovoidTimeout);
        }

        this.checkVisibleAutovoid();
    }

    onFilesAdd() {
        (<any>this.$refs).files.click();
    }

    onFilesClear() {
        (<any>this.$refs).files.value = '';
        this.selectedFiles = [];
    }

    onFilesChange() {
        this.selectedFiles = (<any>this.$refs).files.files;
    }

    onCategoryChange(val) {
        if (val == 'flight_refund') {
            (<any>this).$ga.sendEvent('refund.tickets', 'select.topic', 'refund.tickets.select.topic.support.new', this.gaParams);
        }

        this.checkVisibleAutovoid();
    }

    onAcceptCancelingOrder() {
        this.$auth.createVoidSupport(this.model.orderNumber, this.model.email, this.model.description).then(
            () => {
                this.$auth.getProcessStartedText(G2S.config.locale).then(
                    (result) => {
                        (<any>this.$refs).dialog.hide();
                        (<any>this.$root).$refs.messageDialog.show({
                            title: result.headerText,
                            message: result.messageText,
                            ok: result.buttonAcceptText,
                            onOkCallback: () => {
                                window.location.href = '/';
                            }
                        });
                    },
                    (error) => {
                        (<any>this.$refs).dialog.hide();
                        (<any>this.$root).$refs.messageDialog.show({
                            message: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                        });
                    }
                );
            },
            (error) => {
                (<any>this.$refs).dialog.hide();
                (<any>this.$root.$refs).messageDialog.show({
                    message: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                });
            }
        );
    }

    onClose() {
        (<any>this.$refs).dialog.hide();
    }

    onSubmit() {
        (<any>this).$ga.sendEvent('support.new', 'btn.submit', 'support.new.btn.submit', this.gaParams);
        (<any>this).$ga.reachGoal('support.new.submit');

        this.checkErrors();

        if (this.model.error) {
            (<any>this.$root).$refs.messageDialog.show({
                title: i18next.t('error'),
                message: this.model.error
            });
            (<any>this).$ga.sendEvent('support.new', 'error', 'support.new.error', this.gaParams);

            return;
        }

        const getRefundInfo = () => {
            this.$auth.getRefundInfoWithWriteUs(G2S.config.locale, this.model.orderNumber).then(
                (result) => {
                    this.model.submitted = false;
                    this.refundInfo = <api.RefundInfo>result;
                    (<any>this.$refs).dialog.show();
                },
                (error) => this.submitTicket()
            );
        };

        if (this.isVisibleAutovoid) {
            this.model.submitted = true;
            if (this.$auth.currentUser) {
                this.$auth.ordersExists(this.model.orderNumber).then(
                    (result) => {
                        if (result.orderExists) {
                            getRefundInfo();
                        } else {
                            this.submitTicket();
                        }
                    },
                    (error) => this.submitTicket()
                );
            } else {
                new api.SupportService().checkReservationEmail(this.model.orderNumber, this.model.email).then(
                    (result) => {
                        if (result.isSameEmail) {
                            getRefundInfo();
                        } else {
                            this.submitTicket();
                        }
                    },
                    (error) => this.submitTicket()
                );
            }
        } else {
            this.submitTicket();
        }
    }
}
